import { useDeleteMemory, useMyMemories } from '@/hooks/queries/memories';
import { useMyVariables } from '@/hooks/queries/variables/variables-v2';
import type { Memory } from '@/types/memories';
import { getProfileFormVariableNames } from '@/utils/profile-variables';
import { normalizeProfileVariableName } from '@/utils/soulmap-profile';
import { Icon } from '@iconify/react';
import { Button, ScrollShadow, Spinner } from '@nextui-org/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

interface MyMemoriesPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onBackToProfile: () => void;
}

// Deterministic color palette for name badges
const BADGE_COLORS = [
  { bg: 'bg-red-50', text: 'text-red-600' },
  { bg: 'bg-blue-50', text: 'text-blue-600' },
  { bg: 'bg-green-50', text: 'text-green-600' },
  { bg: 'bg-purple-50', text: 'text-purple-600' },
  { bg: 'bg-amber-50', text: 'text-amber-600' },
  { bg: 'bg-pink-50', text: 'text-pink-600' },
  { bg: 'bg-teal-50', text: 'text-teal-600' },
  { bg: 'bg-orange-50', text: 'text-orange-600' },
];

const VALUE_TRUNCATE_LENGTH = 150;

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

function getBadgeColor(name: string) {
  return BADGE_COLORS[hashString(name) % BADGE_COLORS.length];
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const day = d.getDate();
  const month = d.toLocaleString('en-GB', { month: 'short' });
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const mins = String(d.getMinutes()).padStart(2, '0');
  return `${day} ${month} ${year}, ${hours}:${mins}`;
}

function formatName(name: string): string {
  return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function ValueDisplay({ memory }: { memory: Memory }) {
  const [expanded, setExpanded] = useState(false);
  const { type, value } = memory;

  if (type === 'list' && Array.isArray(value)) {
    return (
      <div className="flex flex-wrap gap-1.5">
        {value.map((item: string, i: number) => (
          <span
            key={i}
            className="inline-block px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700"
          >
            {item}
          </span>
        ))}
      </div>
    );
  }

  if (type === 'boolean') {
    return <p className="text-sm text-gray-700">{value ? 'Yes' : 'No'}</p>;
  }

  const text = String(value ?? '');
  const isLong = text.length > VALUE_TRUNCATE_LENGTH;
  const displayText =
    isLong && !expanded ? text.slice(0, VALUE_TRUNCATE_LENGTH) + '...' : text;

  return (
    <div>
      <p className="text-sm text-gray-700 whitespace-pre-wrap">{displayText}</p>
      {isLong && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-blue-600 hover:text-blue-800 mt-1"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}

function MemoryCard({
  memory,
  onDelete,
}: {
  memory: Memory;
  onDelete: (id: number) => void;
}) {
  const color = getBadgeColor(memory.name);
  const displayName = formatName(memory.name);
  const hasFooter = memory.captured_by_agent_name || memory.captured_at;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      className="border border-gray-200 rounded-xl p-4 flex flex-col gap-3 bg-white"
    >
      {/* Top row: badge + delete */}
      <div className="flex items-start justify-between">
        <span
          className={`inline-block px-2.5 py-0.5 text-xs font-medium rounded-md ${color.bg} ${color.text}`}
        >
          {displayName}
        </span>
        <button
          onClick={() => onDelete(memory.id)}
          className="text-red-400 hover:text-red-600 transition-colors p-0.5 -mt-0.5 -mr-0.5"
          aria-label={`Delete ${displayName}`}
        >
          <Icon icon="proicons:delete" className="w-4 h-4" />
        </button>
      </div>

      {/* Value */}
      <ValueDisplay memory={memory} />

      {/* Footer: agent + date */}
      {hasFooter && (
        <div className="flex items-center gap-2 text-xs text-gray-400">
          {memory.captured_by_agent_name && (
            <>
              <Icon
                icon="mdi:map-marker"
                className="w-3.5 h-3.5 text-red-400 shrink-0"
              />
              <span>{memory.captured_by_agent_name}</span>
            </>
          )}
          {memory.captured_by_agent_name && memory.captured_at && (
            <span className="mx-1">&middot;</span>
          )}
          {memory.captured_at && <span>{formatDate(memory.captured_at)}</span>}
        </div>
      )}
    </motion.div>
  );
}

export function MyMemoriesPanel({
  isOpen,
  onClose,
  onBackToProfile,
}: MyMemoriesPanelProps) {
  const {
    data: memoriesData,
    isLoading: isMemoriesLoading,
    refetch,
  } = useMyMemories({
    page_size: 100,
  });
  const { data: variablesData, isLoading: isVariablesLoading } =
    useMyVariables();
  const isLoading = isMemoriesLoading || isVariablesLoading;
  const { mutate: deleteMemory, isPending: isDeletePending } =
    useDeleteMemory();
  const [activeFilter, setActiveFilter] = useState<string>('All');
  const [deleteTarget, setDeleteTarget] = useState<number | null>(null);
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Refetch every time the panel opens to avoid stale data
  useEffect(() => {
    if (isOpen) refetch();
  }, [isOpen]);

  // Fields already shown in the User Profile form are not repeated here
  const profileFormVariableNames = useMemo(
    () => getProfileFormVariableNames(variablesData),
    [variablesData]
  );

  const memories: Memory[] = useMemo(() => {
    if (!memoriesData) return [];
    const results = Array.isArray(memoriesData)
      ? memoriesData
      : (memoriesData.results ?? []);
    return results.filter(
      (m) => !profileFormVariableNames.has(normalizeProfileVariableName(m.name))
    );
  }, [memoriesData, profileFormVariableNames]);

  // Unique names for filter chips
  const filterNames = useMemo(() => {
    const names = [...new Set(memories.map((m) => m.name))];
    return names.map(formatName);
  }, [memories]);

  // Filtered memories
  const filteredMemories = useMemo(() => {
    if (activeFilter === 'All') return memories;
    return memories.filter((m) => formatName(m.name) === activeFilter);
  }, [memories, activeFilter]);

  const handleDeleteRequest = (id: number) => {
    setDeleteTarget(id);
  };

  const handleConfirmDelete = () => {
    if (deleteTarget === null) return;
    const id = deleteTarget;
    setDeleteTarget(null);
    deleteMemory(id, {
      onSuccess: () => {
        const remaining = filteredMemories.filter((m) => m.id !== id);
        if (remaining.length === 0 && activeFilter !== 'All') {
          setActiveFilter('All');
        }
      },
    });
  };

  return (
    <>
      {createPortal(
        <AnimatePresence>
          {isOpen && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClose}
                className="fixed inset-0 bg-black/50 z-[9999]"
              />

              {/* Panel */}
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                className="fixed top-3 right-3 bottom-3 w-[calc(100%-1.5rem)] sm:w-[400px] bg-white z-[10000] flex flex-col shadow-2xl rounded-2xl overflow-hidden"
                style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
              >
                {/* Header */}
                <div className="flex justify-between items-center px-4 py-3 border-b shrink-0">
                  <span className="text-lg font-semibold">My Memories</span>
                  <Button
                    isIconOnly
                    variant="light"
                    onPress={onClose}
                    size="sm"
                  >
                    <Icon icon="mdi:close" className="h-5 w-5" />
                  </Button>
                </div>

                {/* Scrollable Content */}
                <ScrollShadow className="flex-1 overflow-y-auto">
                  <div className="flex flex-col gap-4 p-4">
                    {/* Back to Profile */}
                    <button
                      onClick={onBackToProfile}
                      className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 transition-colors w-fit"
                    >
                      <Icon icon="mdi:chevron-left" className="w-4 h-4" />
                      Back to Profile
                    </button>

                    {/* Info banner */}
                    <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3.5">
                      <p className="text-sm text-indigo-800 leading-relaxed">
                        These are the things LiFT has learned about you from
                        your conversations. This information helps personalize
                        your experience across all agents. You can delete any
                        memory that's no longer relevant.
                      </p>
                    </div>

                    {isLoading ? (
                      <div className="flex justify-center py-12">
                        <Spinner size="md" />
                      </div>
                    ) : memories.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-12 text-center">
                        <Icon
                          icon="mdi:brain"
                          className="w-12 h-12 text-gray-300 mb-3"
                        />
                        <p className="text-sm text-gray-500">
                          No memories yet. As you chat with agents, they'll
                          remember important things here.
                        </p>
                      </div>
                    ) : (
                      <>
                        {/* Filter chips — max 2 rows collapsed */}
                        {(() => {
                          const MAX_VISIBLE = 5;
                          const allChips = ['All', ...filterNames];
                          const visibleChips = filtersExpanded
                            ? allChips
                            : allChips.slice(0, MAX_VISIBLE);
                          const hiddenCount = allChips.length - MAX_VISIBLE;

                          return (
                            <div className="flex flex-wrap gap-2">
                              {visibleChips.map((name) => (
                                <button
                                  key={name}
                                  onClick={() => setActiveFilter(name)}
                                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                                    activeFilter === name
                                      ? 'bg-gray-900 text-white'
                                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                  }`}
                                >
                                  {name}
                                </button>
                              ))}
                              {!filtersExpanded && hiddenCount > 0 && (
                                <button
                                  onClick={() => setFiltersExpanded(true)}
                                  className="px-3 py-1.5 text-xs font-medium rounded-full bg-gray-100 text-blue-600 hover:bg-gray-200 transition-colors"
                                >
                                  +{hiddenCount} more
                                </button>
                              )}
                              {filtersExpanded &&
                                allChips.length > MAX_VISIBLE && (
                                  <button
                                    onClick={() => setFiltersExpanded(false)}
                                    className="px-3 py-1.5 text-xs font-medium rounded-full bg-gray-100 text-blue-600 hover:bg-gray-200 transition-colors"
                                  >
                                    Show less
                                  </button>
                                )}
                            </div>
                          );
                        })()}

                        {/* Count */}
                        <p className="text-sm text-gray-400">
                          {filteredMemories.length}{' '}
                          {filteredMemories.length === 1
                            ? 'memory'
                            : 'memories'}
                        </p>

                        {/* Memory cards */}
                        <AnimatePresence mode="popLayout">
                          {filteredMemories.length === 0 ? (
                            <motion.p
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              className="text-sm text-gray-400 text-center py-6"
                            >
                              No memories in this category.
                            </motion.p>
                          ) : (
                            filteredMemories.map((memory) => (
                              <MemoryCard
                                key={memory.id}
                                memory={memory}
                                onDelete={handleDeleteRequest}
                              />
                            ))
                          )}
                        </AnimatePresence>
                      </>
                    )}
                  </div>
                </ScrollShadow>
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
      {/* Delete confirmation — custom portal to sit above the drawer */}
      {deleteTarget !== null &&
        createPortal(
          <>
            <div
              className="fixed inset-0 bg-black/50 z-[10001]"
              onClick={() => setDeleteTarget(null)}
            />
            <div className="fixed inset-0 z-[10002] flex items-center justify-center p-4">
              <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 flex flex-col gap-4">
                <h3 className="text-lg font-semibold">Delete Confirmation</h3>
                <p className="text-sm text-gray-600">
                  Are you sure you want to delete this memory? This action
                  cannot be undone.
                </p>
                <div className="flex justify-end gap-2 mt-2">
                  <Button variant="light" onPress={() => setDeleteTarget(null)}>
                    Cancel
                  </Button>
                  <Button
                    color="danger"
                    onPress={handleConfirmDelete}
                    isLoading={isDeletePending}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
}
